import React, { useState, useRef } from "react";
import { Store, CheckSquare, Square, AlertCircle } from "lucide-react"; // Added icons for Select All

function MyRinggit() {
  return (
    <span
      className="flex items-center text-[#FF8C42]"
      style={{ fontWeight: 'bold', fontSize: '18px' }}>
      RM
    </span>
  );
}

function LeftPanel({
  budget,
  setBudget,
  selectedStore,
  setSelectedStore,
  selectedOptions,
  selectAllOptions,
  toggleOption,
  generateBudgetList,
  categoryLabels,
}) {

  //Track if user clicked the button
  const [showErrors, setShowErrors] = useState(false);
  //Create Reference (anchors)
  const budgetRef = useRef(null);
  const categoryRef = useRef(null);
  //Select All
  const allKeys = Object.keys(categoryLabels);
  //Check if every single category is currently true
  const isAllSelected = allKeys.every((key) => selectedOptions[key]);
  //verify
  const isBudgetValid = budget && parseFloat(budget) > 0; //dynamic/disabled button
  const isCategorySelected = Object.values(selectedOptions).some((val) => val === true);
  const isReady = isBudgetValid && isCategorySelected;

  const handleSelectAll = () => {
    const newSelection = {};
    if (isAllSelected) {
      // If all are selected, clear and set to false
      allKeys.forEach(key => newSelection[key] = false);
    } else {
      // If some/none are selected, select all and set all to true
      allKeys.forEach(key => newSelection[key] = true);
    }
    // Update the state all at once
    selectAllOptions(newSelection);
  };

  const handleGenerateClick = () => {
    // 1. Show the errors (Red boxes appear)
    setShowErrors(true);

    // 2. Wait 100 milliseconds for the screen to update, then scroll
    setTimeout(() => {
      if (!isBudgetValid) {
        // Scroll to Budget
        budgetRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest"
        });
      } else if (!isCategorySelected) {
        // Scroll to Category
        categoryRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest"
        });
      } else {
        // If no errors, reset and go!
        setShowErrors(false);
        generateBudgetList();
      }
    }, 100);
  }

  return (
    <div className="mt-6 px-0 sm:px-6 relative pb-24">
      <div
        className="
          bg-gradient-to-br from-[#FFF7E6] via-[#FFECCF] to-[#F9DBA9]
          w-full mx-auto
          p-6 sm:p-8
          rounded-2xl border border-[#FFD8A6]
          shadow space-y-8
        "
      >
        {/* BUDGET */}
        <div ref={budgetRef} className="scroll-mt-24 m-0">
          <label className="flex items-center gap-1 font-bold text-lg text-[#5A3E2B]">
            <MyRinggit />
            <span className="ms-1" style={{ fontSize: '18px' }}>Budget</span>
          </label>
          <input
            type="number"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="Enter your budget (e.g. 150)"
            className={`
              mt-1 w-full p-3 rounded-xl
              focus:outline-none focus:ring-2
              transition-all duration-200
              ${showErrors && !isBudgetValid
                ? "border-2 border-red-500 focus:ring-red-200 bg-red-50 text-red-600" //if 0 (error)
                : "border border-[#E8D1B7] focus:ring-[#FF8C42]" //normal

              }`
            }
          />
          {/* BUDGET ERROR MESSAGE */}
          {showErrors && !isBudgetValid && (
            <p className="flex items-center gap-1 text-red-600 text-sm mt-2 font-bold animate-pulse">
              <AlertCircle size={16} />
              Please enter a valid amount!
            </p>
          )}
        </div>

        {/* STORE */}
        <div>
          <label className="flex items-center gap-1 font-bold text-lg text-[#5A3E2B]">
            <Store className="ms-1 w-6 h-6 text-[#FF8C42]" />
            <span className="ms-1" style={{ fontSize: '1.0' }}>Choose Store</span>
          </label>
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            className="
              mt-1 w-full p-3 rounded-xl
              border border-[#E8D1B7]
              focus:outline-none focus:ring-2 focus:ring-[#FF8C42]
              bg-white
            "
          >
            <option value="all">All Stores</option>
            <option value="FreshMart">FreshMart</option>
            <option value="ValueGrocer">ValueGrocer</option>
            <option value="Premium Foods">Premium Foods</option>
            <option value="The Daily Basket">The Daily Basket</option>
          </select>
        </div>

        {/* CATEGORIES */}
        <div ref={categoryRef} className="space-y-4 scroll-mt-24">

          {/* HEADER WITH SELECT ALL TOGGLE */}
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg text-[#5A3E2B] comic-font">
              Categories
            </h3>

            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm font-bold text-[#FF8C42] hover:text-[#d66e29] transition"
            >
              {isAllSelected ? (
                <>
                  <CheckSquare size={18} /> Deselect All
                </>
              ) : (
                <>
                  <Square size={18} /> Select All
                </>
              )}
            </button>
          </div>

          {/* CATEGORY ERROR MESSAGE */}
          {showErrors && !isCategorySelected && (
            <p className="flex items-center gap-1 text-red-600 text-sm font-bold animate-pulse">
              <AlertCircle size={16} />
              Please select at least one category!
            </p>
          )}

          <div className={`
            grid grid-cols-2 sm:grid-cols-4 gap-4 p-2 rounded-2xl transition-all
            ${showErrors && !isCategorySelected ? "bg-red-50 border-2 border-red-300 border-dashed" : ""}
            `}>

            {Object.keys(categoryLabels).map((key) => {
              const isSelected = selectedOptions[key];

              return (
                <div
                  key={key}
                  onClick={() => toggleOption(key)}
                  className={`
                      cursor-pointer rounded-2xl p-4
                      flex flex-col items-center text-center
                      transition-all duration-200
                      border
                      ${isSelected
                      ? "bg-[#FFE3C4] border-[#FF8C42] shadow-md scale-[1.02]"
                      : "bg-white border-[#E8D1B7] hover:bg-[#FFF1DB]"
                    }
                    `}
                >
                  {/* IMAGE */}
                  <div
                    className={`
                        w-24 h-24 rounded-xl
                        flex items-center justify-center
                        mb-3
                        ${isSelected
                        ? "bg-[#FF8C42]"
                        : "bg-[#F3E6D3]"
                      }
                      `}
                  >
                    <img
                      src={`/categories/${key}.png`}
                      alt={categoryLabels[key]}
                      className="w-20 h-20 object-contain"
                    />
                  </div>

                  {/* LABEL */}
                  <span className="
                      comic-font
                      font-bold
                      text-sm sm:text-base
                      text-[#5A3E2B]
                      leading-tight
                    ">
                    {categoryLabels[key]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* GENERATE BUTTON */}
        <div className="sticky bottom-4 z-20 pt-4">
          <button
            onClick={handleGenerateClick}
            className={`w-full py-4 rounded-xl font-bold text-lg
              shadow-lg transition-all duration-300
              hover:brightness-110 active:scale-95 cursor-pointer
              ${isReady
                ? "bg-[#FF8C00] text-white hover:brightness-110 active?:scale-95 cursor-pointer"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
          >
            {isReady ? "Generate Suggestions →" : "Generate Suggestions →"}
          </button>
        </div>

      </div>
    </div>
  );
}

export default LeftPanel;
